import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AssignmentSurveyorState, AssignmentTeamState, OrderAssignment } from '../../00_modules/disposition/models';
import { AssignmentBusinessPartnerState } from '../../00_modules/disposition/models/AssignmentBusinessPartnerState';
import { patch, updateItem } from '@ngxs/store/operators';
import { AddAssignments, DeleteAssignments, ResetAssignments, ToggleSurveyorSelection, UpdateAssignments } from '../action/assignment.action';

export interface AssignmentStateModel {
    teamId: number;
    teamName: string;
    businessPartnerId: number;
    businessPartnerName: string;
    teamAssignmentState: AssignmentTeamState;
    businessPartnerAssignmentState: AssignmentBusinessPartnerState;
    surveyorId: number;
    surveyorName: string;
    surveyorFirstName: string;
    surveyorLastName: string;
    surveyorAssignmentState: AssignmentSurveyorState;
    rejectionReason: string;
    timestamp: string;
    isModified: boolean;
    selectId: string;
    sortComparer: boolean;
}

function getAssignmentIdFromOrderAssignment(assignment: OrderAssignment): string {
    return getAssignmentId(assignment.businessPartnerId, assignment.teamId, assignment.surveyorId);
}

function getAssignmentId(businessPartnerId: number, teamId: number, surveyorId: number): string {
    return `${businessPartnerId}${getFormatedId(teamId)}${getFormatedId(surveyorId)}`;
}

function getFormatedId(id: number | undefined): string {
    return id ? `-${id}` : '';
}

export interface AssignmentsStateModel {
    assignments: AssignmentStateModel[];
}
@State<AssignmentsStateModel>({
    name: 'assignments',
    defaults: { assignments: [] },
})
@Injectable()
export class AssignmentState {
    @Selector()
    static getAssignments(state: AssignmentsStateModel): any {
        return state.assignments;
    }

    @Action(AddAssignments)
    addAssignments(ctx: StateContext<AssignmentsStateModel>, action: AddAssignments) {
        let assignmentsStateModel: AssignmentStateModel[] = [];

        action.orderAssignments.forEach(oa => {
            let orderAssignment = {
                ...oa,
                selectId: getAssignmentIdFromOrderAssignment(oa),
                sortComparer: false,
            };

            assignmentsStateModel.push(orderAssignment);
        });

        let assignments: AssignmentStateModel[] = [...ctx.getState().assignments];
        assignments.push(...assignmentsStateModel.filter(entry => assignments.findIndex(x => x.selectId === entry.selectId) < 0));

        assignmentsStateModel.forEach(entry => {
            let index = assignments.findIndex(x => x.selectId == entry.selectId);
            if (index > -1) {
                let state =
                    assignments[index].surveyorAssignmentState === 1 || entry.surveyorAssignmentState === 1 ? AssignmentSurveyorState.Offered : entry.surveyorAssignmentState;
                assignments[index] = Object.assign({}, entry, { surveyorAssignmentState: state });
            }
        });

        ctx.setState(
            patch<AssignmentsStateModel>({
                assignments: assignments,
            })
        );
    }

    @Action(DeleteAssignments)
    deleteAssignments(ctx: StateContext<AssignmentsStateModel>, action: DeleteAssignments) {
        let state = ctx.getState().assignments.slice();

        state = state.filter(assignment => !action.orderAssignmentIds.find(x => x == assignment.selectId));

        ctx.patchState({
            assignments: state,
        });
    }

    @Action(UpdateAssignments)
    updateAssignments(ctx: StateContext<AssignmentsStateModel>, action: UpdateAssignments) {
        const state = ctx.getState().assignments.slice();

        for (const orderAssignment of action.orderAssignments) {
            let selectId = getAssignmentIdFromOrderAssignment(orderAssignment);
            const orderAssignmentIndexId = state.findIndex(x => x.selectId == selectId);

            let assignmentsStateModel = {
                ...orderAssignment,
                selectId: selectId,
                sortComparer: false,
            };
            state[orderAssignmentIndexId] = assignmentsStateModel;
        }

        ctx.patchState({
            assignments: state,
        });
    }

    @Action(ToggleSurveyorSelection)
    toggleSurveyorSelection(ctx: StateContext<AssignmentsStateModel>, action: ToggleSurveyorSelection) {
        const surveyorAssignmentState = action.isSelected ? AssignmentSurveyorState.Offered : AssignmentSurveyorState.None;

        let assignmentId = getAssignmentId(action.businessPartnerId, action.teamId, action.surveyorId);

        const assignment = ctx.getState().assignments.find(x => x.selectId === assignmentId);

        let newAssignment: AssignmentStateModel = {
            ...assignment,
            businessPartnerId: action.businessPartnerId,
            teamId: action.teamId,
            surveyorId: action.surveyorId,
            surveyorAssignmentState: surveyorAssignmentState,
            isModified: true,
        };

        ctx.setState(
            patch<AssignmentsStateModel>({
                assignments: updateItem<AssignmentStateModel>(asm => asm.selectId === assignmentId, newAssignment),
            })
        );
    }

    @Action(ResetAssignments)
    resetAssignments(ctx: StateContext<AssignmentsStateModel>) {
        ctx.setState({ assignments: [] });
    }
}
